import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { Fighter } from '../../../../../../corelogic/domain/models/fighter.model';
import { RecentFightResult } from '../../../../../../corelogic/domain/models/recentFightResult.model';
import { capitalizeFirstLetter } from '../../../../../../corelogic/domain/utils/capitalize.utils';
import {
  calculateAge,
  convertToFeetInches,
  convertToInches,
} from '../../../../../../corelogic/domain/utils/fighter.utils';
import { getWeightDisplayName } from '../../../../../../corelogic/domain/utils/weightUtils';
import { useFighterImageState } from '../../../hooks/useFighterImage';
import { FighterImageService } from '../../../services/FighterImage.service';
import FightResultCircle from '../../Shared/FightResultCircle/FightResultCircle';

interface SelectorModalCardProps {
  fighter: Fighter;
  color: string;
  mostRecentFightResults: RecentFightResult[];
}

const SelectorModalCard: React.FC<SelectorModalCardProps> = ({
  fighter,
  color,
  mostRecentFightResults,
}) => {
  const { fighterImage, isLoading } = useFighterImageState(
    fighter.firstname,
    fighter.lastname,
    fighter.dob,
  );

  // Request fighter image when component mounts or fighter changes
  useEffect(() => {
    FighterImageService.retrieveFighterImage(
      fighter.firstname,
      fighter.lastname,
      fighter.dob,
      fighter.gender,
    );
  }, [fighter.firstname, fighter.lastname, fighter.dob, fighter.gender]);

  return (
    <Card
      sx={{
        width: '92%',
        maxHeight: '100%',
        textAlign: 'center',
        bgcolor: 'rgba(20, 20, 20, 0.95)',
        alignItems: 'center',
        flexDirection: 'column',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
      }}
    >
      <Box
        sx={{
          bgcolor: color,
          padding: { xs: '12px', sm: '8px' },
          borderRadius: '16px 16px 0 0',
        }}
      >
        <Typography
          variant="h4"
          component="h4"
          sx={{
            color: 'white',
            fontFamily: 'Rubik Mono One',
            fontSize: { xs: '0.8rem', sm: 'clamp(0.8rem, 1.0vw, 1.3rem)' },
          }}
        >
          {fighter.firstname} {fighter.lastname}
        </Typography>
      </Box>

      <Box
        sx={{
          padding: { xs: '10px 0 6px', sm: '20px 0 10px' },
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          height: { xs: '120px', sm: '120px' },
          position: 'relative',
        }}
      >
        {!isLoading && fighterImage && (
          <Box
            component="img"
            sx={{
              maxWidth: { xs: '60%', sm: '90%' },
              maxHeight: { xs: '60%', sm: '90%' },
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              objectPosition: 'center center',
              position: 'relative',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            src={fighterImage}
            alt="Fighter Image"
          />
        )}
      </Box>

      <CardContent
        sx={{
          padding: { xs: '0 12px 12px', sm: '0 8px 8px' },
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Record and Division Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '90%',
            margin: '0 auto 15px',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: '8px', sm: 0 },
          }}
        >
          <Box sx={{ width: { xs: '100%', sm: '45%' }, textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: {
                  xs: '0.65rem',
                  sm: 'clamp(0.4rem, 0.6vw, 0.7rem)',
                },
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              RECORD
            </Typography>
            <Typography
              sx={{
                fontSize: {
                  xs: '0.8rem',
                  sm: 'clamp(0.7rem, 1.0vw, 1.2rem)',
                },
                fontWeight: 'bold',
              }}
            >
              {fighter.wins}-{fighter.losses}-{fighter.draws}
            </Typography>
          </Box>

          <Box sx={{ width: { xs: '100%', sm: '52%' }, textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: {
                  xs: '0.65rem',
                  sm: 'clamp(0.4rem, 0.6vw, 0.7rem)',
                },
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              DIVISION
            </Typography>
            <Typography
              sx={{
                fontSize: {
                  xs: '0.8rem',
                  sm: 'clamp(0.7rem, 1.0vw, 1.2rem)',
                },
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {getWeightDisplayName(fighter.weight)}
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            width: '85%',
            mx: 'auto',
            mb: 2,
          }}
        />

        {/* Stats Section */}
        <Box
          sx={{
            width: { xs: '90%', sm: '80%' },
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '8px', sm: '12px' },
          }}
        >
          {/* Age and Gender row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: { xs: '8px', sm: '12px' },
            }}
          >
            <Box sx={{ width: '48%', textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: {
                    xs: '0.65rem',
                    sm: 'clamp(0.4rem, 0.6vw, 0.7rem)',
                  },
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                AGE
              </Typography>
              <Typography
                sx={{
                  fontSize: {
                    xs: '0.8rem',
                    sm: 'clamp(0.6rem, 0.8vw, 1.0rem)',
                  },
                }}
              >
                {fighter.dob ? calculateAge(fighter.dob.toString()) : 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ width: '48%', textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: {
                    xs: '0.65rem',
                    sm: 'clamp(0.4rem, 0.6vw, 0.7rem)',
                  },
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                GENDER
              </Typography>
              <Typography
                sx={{
                  fontSize: {
                    xs: '0.8rem',
                    sm: 'clamp(0.6rem, 0.8vw, 1.0rem)',
                  },
                }}
              >
                {capitalizeFirstLetter(fighter.gender)}
              </Typography>
            </Box>
          </Box>

          {/* Height and Reach row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: { xs: '8px', sm: '12px' },
            }}
          >
            <Box sx={{ width: '48%', textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: {
                    xs: '0.65rem',
                    sm: 'clamp(0.4rem, 0.6vw, 0.7rem)',
                  },
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                HEIGHT
              </Typography>
              <Typography
                sx={{
                  fontSize: {
                    xs: '0.8rem',
                    sm: 'clamp(0.6rem, 0.8vw, 1.0rem)',
                  },
                }}
              >
                {convertToFeetInches(fighter.height)}
              </Typography>
            </Box>

            <Box sx={{ width: '48%', textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: {
                    xs: '0.65rem',
                    sm: 'clamp(0.4rem, 0.6vw, 0.7rem)',
                  },
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                REACH
              </Typography>
              <Typography
                sx={{
                  fontSize: {
                    xs: '0.8rem',
                    sm: 'clamp(0.6rem, 0.8vw, 1.0rem)',
                  },
                }}
              >
                {convertToInches(fighter.reach)}"
              </Typography>
            </Box>
          </Box>

          {/* Stance and Nickname container */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              gap: { xs: '8px', sm: '12px' },
              width: '100%',
            }}
          >
            {/* Stance */}
            <Box sx={{ width: { xs: '100%', sm: '48%' }, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: {
                    xs: '0.65rem',
                    sm: 'clamp(0.4rem, 0.6vw, 0.7rem)',
                  },
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                STANCE
              </Typography>
              <Typography
                sx={{
                  fontSize: {
                    xs: '0.8rem',
                    sm: 'clamp(0.6rem, 0.8vw, 1.0rem)',
                  },
                }}
              >
                {fighter.stance || 'N/A'}
              </Typography>
            </Box>

            {/* Nickname */}
            <Box sx={{ width: { xs: '100%', sm: '48%' }, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: {
                    xs: '0.65rem',
                    sm: 'clamp(0.4rem, 0.6vw, 0.7rem)',
                  },
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                NICKNAME
              </Typography>
              <Typography
                sx={{
                  fontSize: {
                    xs: '0.8rem',
                    sm: 'clamp(0.6rem, 0.8vw, 1.0rem)',
                  },
                }}
              >
                {fighter.nickname ? fighter.nickname : 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            width: '85%',
            mx: 'auto',
            my: 2,
          }}
        />

        {/* Recent Fight Results Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 'auto',
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: '0.75rem',
                sm: 'clamp(0.4rem, 0.6vw, 0.7rem)',
              },
              color: 'rgba(255,255,255,0.7)',
              mr: 1,
            }}
          >
            RECENT:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {mostRecentFightResults.map((result, index) => (
              <FightResultCircle
                key={index}
                result={result.result}
                isTitleFight={result.isTitleFight}
              />
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SelectorModalCard;
